import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { api, resolveImage, mailto } from "../lib/api";
import ClassicCard from "../components/ClassicCard";
import Seo from "../components/Seo";

export default function RoomDetail() {
  const { slug } = useParams();
  const { lang, t } = useI18n();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    (async () => {
      try {
        const r = await api.get(`/rooms/${slug}`);
        setData(r.data);
      } catch (e) {
        if (e.response?.status === 404) setNotFound(true);
      }
    })();
  }, [slug]);

  if (notFound) {
    return (
      <div className="stoel-container py-32 text-center">
        <p className="text-stoel-inkSoft">404</p>
        <Link to="/ruimtes" className="stoel-link mt-4 inline-block">
          {t.common.backTo} {t.common.rooms}
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="stoel-container py-32 animate-pulse space-y-6">
        <div className="aspect-[16/9] bg-stoel-paperDeep" />
        <div className="h-10 w-1/2 bg-stoel-paperDeep" />
      </div>
    );
  }

  const room = data.item;
  const classics = data.classics || [];
  const title = lang === "nl" ? room.title_nl : room.title_en;
  const intro = lang === "nl" ? room.intro_nl : room.intro_en;

  return (
    <div data-testid="page-room-detail">
      <Seo
        path={`/ruimtes/${room.slug}`}
        titleNl={room.title_nl}
        titleEn={room.title_en}
        descNl={(room.intro_nl || "").split(".").slice(0, 2).join(".") + "."}
        descEn={(room.intro_en || "").split(".").slice(0, 2).join(".") + "."}
        ogImage={room.hero_image_url ? `https://destoel.com${room.hero_image_url}` : undefined}
        ogType="article"
      />
      <div className="relative h-[60vh] min-h-[420px] overflow-hidden">
        <img
          src={resolveImage(room.hero_image_url)}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.src = resolveImage(null);
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-stoel-ink/30 via-stoel-ink/10 to-stoel-paper" />
        <div className="relative stoel-container h-full flex flex-col justify-end pb-12">
          <Link
            to="/ruimtes"
            data-testid="back-to-rooms"
            className="self-start inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-stoel-paper bg-stoel-ink/40 backdrop-blur px-3 py-1.5 mb-6 hover:bg-stoel-ink/70"
          >
            <ArrowLeft size={14} /> {t.common.backTo} {t.common.rooms}
          </Link>
          <h1
            data-testid="room-title"
            className="stoel-display text-stoel-paper text-5xl md:text-6xl lg:text-7xl"
          >
            {title}
          </h1>
        </div>
      </div>

      <section className="stoel-container py-16 md:py-24 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="stoel-eyebrow">— {t.nav.rooms}</div>
        </div>
        <div className="md:col-span-7">
          <p className="text-2xl md:text-3xl font-light leading-[1.32] tracking-tight whitespace-pre-line">
            {intro}
          </p>
        </div>
      </section>

      <div className="stoel-divider" />

      {classics.length > 0 && (
        <section className="stoel-container py-16 md:py-24">
          <div className="flex items-baseline justify-between mb-10">
            <div>
              <div className="stoel-eyebrow mb-3">— {t.common.featuredIn} {title.toLowerCase()}</div>
              <h2 className="stoel-display text-3xl md:text-4xl">
                {lang === "nl" ? "Geselecteerde stukken" : "Selected pieces"}
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-10">
            {classics.map((c, i) => (
              <ClassicCard key={c.id} item={c} index={i} />
            ))}
          </div>
        </section>
      )}

      <section className="bg-stoel-ink text-stoel-paper">
        <div className="stoel-container py-16 md:py-20 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="max-w-xl">
            <h3 className="text-3xl md:text-4xl font-light tracking-tight">
              {lang === "nl" ? `Een ${title.toLowerCase()} in gedachten?` : `A ${title.toLowerCase()} in mind?`}
            </h3>
            <p className="mt-3 text-stoel-paper/70">
              {lang === "nl" ? "Stuur een briefing en we reageren binnen twee werkdagen." : "Send a brief and we respond within two business days."}
            </p>
          </div>
          <a
            href={mailto(`${lang === "nl" ? "Project" : "Project"}: ${title}`)}
            data-testid="room-inquire-cta"
            className="inline-flex items-center gap-3 px-7 py-4 bg-stoel-terracotta hover:bg-stoel-terracottaDeep transition-colors text-sm tracking-wide"
          >
            {t.common.cta} <ArrowUpRight size={16} />
          </a>
        </div>
      </section>
    </div>
  );
}
